import React from 'react'

import { Box } from '@chakra-ui/react'
import { Navigate, Route, Routes, useParams } from 'react-router'
import { useSelectedTrial } from 'src/features/auth/context'
import { CriteriaDetailScreen } from 'src/features/criteria/components/CriteriaDetailScreen/CriteriaDetailScreen'
import { CriteriaListScreen } from 'src/features/criteria/components/CriteriaListScreen/CriteriaListScreen'
import { MainLayout } from 'src/features/misc/components/MainLayout/MainLayout'
import { NotFound } from 'src/features/misc/components/NotFound/NotFound'
import { useNavigationRoutes } from 'src/features/misc/hooks/useNavigationRoutes'
import { PatientListScreen } from 'src/features/patient/components/PatientListScreen/PatientListScreen'
import { ReferredPatientDetailScreen } from 'src/features/referredPatient/components/ReferredPatientDetialScreen/ReferredPatientDetailScreen'
import { ReferredPatientListScreen } from 'src/features/referredPatient/components/ReferredPatientListScreen/ReferredPatientListScreen'
import { useTrialsWithTrialHospitals } from 'src/features/trial/api/getTrialsWithTrialHospitals'
import { TrialTop } from 'src/features/trial/components/TrialTop/TrialTop'
import { WorksheetDetailScreen } from 'src/features/worksheet/components'
import { useStickyQuery } from 'src/features/worksheet/hooks/useStickyQuery'
import { WSDataExportScreen } from 'src/features/wsdataexport/components/WSDataExportScreen/WSDataExportScreen'
import { PatientDetailLayout } from 'src/layouts/PatientDetailLayout/PatientDetailLayout'
import { routes as rootRoutes } from 'src/legacy_modules/routes'

import { Explanation } from './e-consent/explanation/explanation'
import { IcfDocument } from './e-consent/icf-document/icf-document'
import { Member } from './settings/member/member'
import { useInit } from './settings/use-init'
import { Template } from './template/template'
import { getRelativePath, PathKey } from '../../../../constants/paths'
import { SelectTrialContainer } from '../../select-trial/select-trial-container'
import { Crf } from './settings/crf/crf'

export const trialIdParamName = 'trialUid'

export const TrialDetailContent = () => {
  const { trialUid } = useParams<{ [trialIdParamName]: string }>()
  const { stickyQuery } = useStickyQuery()
  const { selectedTrial } = useSelectedTrial()
  const { data: trials } = useTrialsWithTrialHospitals({})

  const { routeItems } = useNavigationRoutes()

  if (!trials) return null

  if (selectedTrial.uid !== trialUid) {
    // 選択後のpathにstickyが含まれる場合は自動的にtrialを選択する。
    if (trials.some(trial => trial.uid === trialUid) && !!stickyQuery)
      return <SelectTrialContainer redirectWithPathSelection />

    return <Navigate replace to={rootRoutes.dashboard} />
  }

  const getPath = (path: PathKey) => {
    return getRelativePath('Trial', path)
  }

  // 試験TOPは必ず表示するため現状はリダイレクト先として無条件で設定
  const redirectPath = getPath('TrialTop')

  return (
    <Routes>
      <Route
        path="/"
        element={<MainLayout navigationRouteItems={routeItems} />}
      >
        <Route index element={<Navigate to={redirectPath} />} />
        {routeItems.trialTop.isEnabled && (
          <Route
            path={`${getPath('TrialTop')}/*`}
            element={<TrialTopScreen />}
          />
        )}
        {routeItems.patients.isEnabled && (
          <Route
            path={`${getPath('Patients')}/*`}
            element={<PatientRoutes />}
          />
        )}
        {routeItems.explanations.isEnabled && (
          <Route
            path={`${getPath('Explanations')}/*`}
            element={<ExplanationRoutes />}
          />
        )}
        {routeItems.referredPatients.isEnabled && (
          <Route path="referred-patients">
            <Route index element={<ReferredPatientListScreen />} />
            <Route
              path=":referredPatientUid"
              element={<ReferredPatientDetailScreen />}
            />
          </Route>
        )}
        {routeItems.templates.isEnabled && (
          <Route path={`${getPath('Templates')}/*`} element={<Template />} />
        )}
        {routeItems.icfDocuments.isEnabled && (
          <Route
            path={`${getPath('IcfDocuments')}/*`}
            element={<IcfDocumentRoutes />}
          />
        )}
        {routeItems.criteria.isEnabled && (
          <Route path="/criteria">
            <Route index element={<CriteriaListScreen />} />
            <Route path=":criteriaUid" element={<CriteriaDetailScreen />} />
          </Route>
        )}
        {routeItems.account.isEnabled && (
          <Route path={getPath('SettingsMember')} element={<MembersScreen />} />
        )}
        {routeItems.csvExport.isEnabled && (
          <Route
            path={getPath('SettingsWSDataExport')}
            element={<CSVExportScreen />}
          />
        )}
        {routeItems.crf.isEnabled && (
          <Route path={`${getPath('SettingsCrf')}/*`} element={<CRFScreen />} />
        )}
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

const TrialTopScreen: React.FC = () => {
  return (
    <Box h="full">
      <TrialTop />
    </Box>
  )
}

const PatientRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path={`:patientUid/*`} element={<PatientDetailRouter />} />

      <Route path={`/`} element={<PatientListScreen />} />
      <Route path="*" element={<Navigate replace to={`/`} />} />
    </Routes>
  )
}
const PatientDetailRouter: React.FC = () => {
  const { patientUid } = useParams<{
    patientUid: string
  }>()

  if (!patientUid) return null

  return (
    <PatientDetailLayout patientUid={patientUid}>
      <Routes>
        <Route path={`/`} element={<Empty />} />
        <Route
          path={getRelativePath('Patient', 'PatientWorksheet')}
          element={<WorksheetDetailScreen />}
        />
        <Route path="*" element={<Navigate replace to={`/`} />} />
      </Routes>
    </PatientDetailLayout>
  )
}
const Empty = () => null

const ExplanationRoutes: React.FC = () => {
  return (
    <Box h="full">
      <Explanation />
    </Box>
  )
}
const IcfDocumentRoutes: React.FC = () => {
  return (
    <Box h="full" p="0">
      <IcfDocument />
    </Box>
  )
}

const MembersScreen: React.FC = () => {
  const { selectedTrial } = useSelectedTrial()

  // 元はsettingsで行なっていたが新しいナビゲーションの場合settingsが呼ばれないためここで行う
  const { initialized } = useInit(selectedTrial.uid)
  if (!initialized) {
    return null
  }
  return (
    <Box h="full" p="6">
      <Member />
    </Box>
  )
}

const CSVExportScreen: React.FC = () => {
  return (
    <Box h="full" p="6">
      <WSDataExportScreen />
    </Box>
  )
}

const CRFScreen: React.FC = () => {
  return (
    <Box h="full" p="6">
      <Crf />
    </Box>
  )
}
